'use client';

import React, {
  ReactNode,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from 'react';

import * as Accordion from '@radix-ui/react-accordion';
import { useInView } from 'framer-motion';
import { motion } from 'framer-motion';

import { cn } from '@kit/ui/utils';

import GradientNoiseGlow from '~/components/survey-builder/shared/gradient-noise-glow';

import { AboutIcons } from '../icons';
import GlowingBadge from '../ui/glowing-badge';

type AccordionItemProps = {
  children: React.ReactNode;
  className?: string;
} & Accordion.AccordionItemProps;

const AccordionItem = forwardRef<HTMLDivElement, AccordionItemProps>(
  ({ children, className, ...props }, forwardedRef) => (
    <Accordion.Item
      className={cn(
        'overflow-visible focus-within:relative focus-within:z-10',
        className,
      )}
      {...props}
      ref={forwardedRef}
    >
      {children}
    </Accordion.Item>
  ),
);
AccordionItem.displayName = 'AccordionItem';

type AccordionTriggerProps = {
  children: React.ReactNode;
  className?: string;
};

const AccordionTrigger = forwardRef<HTMLButtonElement, AccordionTriggerProps>(
  ({ children, className, ...props }, forwardedRef) => (
    <Accordion.Header className="flex">
      <Accordion.Trigger
        className={cn(
          'group flex flex-1 cursor-pointer items-center justify-between px-5 leading-none outline-none',
          className,
        )}
        {...props}
        ref={forwardedRef}
      >
        {children}
      </Accordion.Trigger>
    </Accordion.Header>
  ),
);
AccordionTrigger.displayName = 'AccordionTrigger';
type AccordionContentProps = {
  children: ReactNode;
  className?: string;
} & Accordion.AccordionContentProps;

const AccordionContent = forwardRef<HTMLDivElement, AccordionContentProps>(
  ({ children, className, ...props }, forwardedRef) => (
    <Accordion.Content
      className={cn(
        'data-[state=closed]:animate-slide-up data-[state=open]:animate-slide-down overflow-hidden text-[15px] font-medium',
        className,
      )}
      {...props}
      ref={forwardedRef}
    >
      <div className="px-5 py-2">{children}</div>
    </Accordion.Content>
  ),
);
AccordionContent.displayName = 'AccordionContent';

const leInnovationImpacts = [
  {
    year: 2020,
    title: 'Founded',
    content: 'We Introduced advanced predictive analytics and the results',
    icon: <AboutIcons.founded />,
  },
  {
    year: 2021,
    title: 'Launched',
    content: 'We Introduced advanced predictive analytics and the results',
    icon: <AboutIcons.launched />,
  },
  {
    year: 2023,
    title: 'Introduced advanced',
    content: 'We Introduced advanced predictive analytics and the results',
    icon: <AboutIcons.introducedAdvanced />,
  },
  {
    year: 2024,
    title: 'Expanded globally',
    content: 'We Introduced advanced predictive analytics and the results',
    icon: <AboutIcons.expandedGlobally />,
  },
  {
    year: 2025,
    title: 'Recognized',
    content: 'We Introduced advanced predictive analytics and the results',
    icon: <AboutIcons.recognized />,
  },
];
const collapseDelay = 5000;

export default function InnovationImpactSection({
  heading,
  content,
  innovationImpacts,
}: {
  heading?: string;
  content?: ReactNode;
  innovationImpacts: {
    tag: string;
    heading: string;
    content: ReactNode;
    icon: ReactNode;
  }[];
}) {
  const [currentIndex, setCurrentIndex] = useState<number>(-1);
  const carouselRef = useRef<HTMLUListElement>(null);
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    amount: 0.5,
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      if (isInView) {
        setCurrentIndex(0);
      } else {
        setCurrentIndex(-1);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [isInView]);

  const scrollToIndex = (index: number) => {
    if (carouselRef.current) {
      const card = carouselRef.current.querySelectorAll('.card')[index];
      if (card) {
        const cardRect = card.getBoundingClientRect();
        const carouselRect = carouselRef.current.getBoundingClientRect();
        const offset =
          cardRect.left -
          carouselRect.left -
          (carouselRect.width - cardRect.width) / 2;

        carouselRef.current.scrollTo({
          left: carouselRef.current.scrollLeft + offset,
          behavior: 'smooth',
        });
      }
    }
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) =>
        prevIndex !== undefined
          ? (prevIndex + 1) % innovationImpacts.length
          : 0,
      );
    }, collapseDelay);

    return () => clearInterval(timer);
  }, [currentIndex]);

  useEffect(() => {
    const handleAutoScroll = () => {
      const nextIndex =
        (currentIndex !== undefined ? currentIndex + 1 : 0) %
        innovationImpacts.length;
      scrollToIndex(nextIndex);
    };

    const autoScrollTimer = setInterval(handleAutoScroll, collapseDelay);

    return () => clearInterval(autoScrollTimer);
  }, [currentIndex]);

  useEffect(() => {
    const carousel = carouselRef.current;
    if (carousel) {
      const handleScroll = () => {
        const scrollLeft = carousel.scrollLeft;
        const cardWidth = carousel.querySelector('.card')?.clientWidth ?? 0;
        const newIndex = Math.min(
          Math.floor(scrollLeft / cardWidth),
          innovationImpacts.length - 1,
        );
        setCurrentIndex(newIndex);
      };

      carousel.addEventListener('scroll', handleScroll);
      return () => carousel.removeEventListener('scroll', handleScroll);
    }
  }, []);

  return (
    <section
      ref={ref}
      className="module-innovation-impact mx-auto max-w-1440 p-4"
    >
      <div className="rounded-[40px] bg-[#141414] pb-10 pt-10 lg:pb-[120px] lg:pt-25">
        <motion.div
          className="mb-4 flex px-6 lg:mb-3 lg:px-20"
          initial={{ opacity: 0, y: 100 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          <GlowingBadge>
            <AboutIcons.evolution />
            <span>Our Evolution</span>
          </GlowingBadge>
        </motion.div>
        <motion.div
          className="flex flex-col justify-between gap-10 px-6 lg:flex-row lg:px-20"
          initial={{ opacity: 0, y: 100 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          <div className="max-w-[518px]">
            <h2 className="text-5xl font-semibold leading-[1.3] lg:text-5xl lg:leading-[1.4]">
              {heading}
            </h2>
          </div>
          <div className="mt-3 max-w-[474px] text-lg leading-[1.5] tracking-[-0.9px] text-white/60">
            {content}
          </div>
        </motion.div>
        <motion.div
          className="relative mt-10 px-4 lg:mt-25 lg:px-0"
          initial={{ opacity: 0, y: 100 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          <div
            className="absolute left-0 top-0 z-10 hidden h-full w-[90px] lg:block"
            style={{
              background:
                'linear-gradient(90deg, #141414 0%, rgba(20, 20, 20, 0.00) 100%)',
            }}
          ></div>
          <Accordion.Root
            className=""
            type="single"
            defaultValue={`item-${currentIndex}`}
            value={`item-${currentIndex}`}
            onValueChange={(value) =>
              setCurrentIndex(Number(value.split('-')[1]))
            }
          >
            <div className="grid grid-cols-1 lg:grid-cols-5">
              {innovationImpacts.map((item, index) => (
                <AccordionItem
                  key={item.tag}
                  value={`item-${index}`}
                  className="relative flex flex-col pl-20 lg:items-center lg:pl-0"
                >
                  <AccordionTrigger className="flex flex-col items-start px-0 lg:items-center lg:px-4">
                    <div
                      className={cn(
                        'text-lg leading-[1.5]',
                        currentIndex === index ? 'text-white' : 'text-white/50',
                      )}
                    >
                      {item.tag}
                    </div>
                    <div className="absolute -left-2 -top-1 z-10 lg:relative lg:left-0 lg:top-0 lg:mt-5">
                      <div className="item-box flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-full bg-neutral-900">
                        <div className="icon-container flex h-[46px] w-[46px] items-center justify-center">
                          <GradientNoiseGlow
                            className="relative flex h-full flex-1 items-center justify-center"
                            visible={currentIndex === index}
                            size="large"
                          >
                            {leInnovationImpacts[index]!.icon}
                          </GradientNoiseGlow>
                        </div>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <div className="relative mt-6 hidden w-full justify-center lg:mt-3 lg:flex">
                    <div className="relative z-20 bg-[#141414] px-6">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="5"
                        height="4"
                        viewBox="0 0 5 4"
                        fill="none"
                      >
                        <circle
                          cx="2.50005"
                          cy="2.00005"
                          r="2.00005"
                          fill="white"
                          fillOpacity={currentIndex === index ? '1' : '0.2'}
                        />
                      </svg>
                    </div>

                    {index === 0 && (
                      <div
                        className={cn(
                          'absolute bottom-0 left-[10%] top-[1px] h-0.5 w-[30%] overflow-hidden rounded-lg bg-neutral-300/50',
                        )}
                      >
                        <div
                          className={cn(
                            'h-full origin-left bg-primary transition-all ease-linear dark:bg-white',
                            currentIndex === innovationImpacts.length - 1
                              ? 'w-full'
                              : 'w-0',
                          )}
                          style={{
                            transitionDelay:
                              currentIndex === innovationImpacts.length - 1
                                ? currentIndex === innovationImpacts.length - 1
                                  ? `${collapseDelay / 2}ms`
                                  : `${collapseDelay}ms`
                                : '0s',
                            transitionDuration:
                              currentIndex === innovationImpacts.length - 1
                                ? currentIndex === innovationImpacts.length - 1
                                  ? `${collapseDelay / 2}ms`
                                  : `${collapseDelay}ms`
                                : '0s',
                          }}
                        ></div>
                      </div>
                    )}
                    <div
                      className={cn(
                        'absolute bottom-0 top-[1px] h-0.5 overflow-hidden rounded-lg bg-neutral-300/50',
                        'right-[-50%] w-[calc(100%-24px)]',
                        index === innovationImpacts.length - 1 &&
                          'right-[10%] w-[30%]',
                      )}
                    >
                      <div
                        className={cn(
                          'h-full origin-left bg-primary transition-all ease-linear dark:bg-white',
                          currentIndex === index ? 'w-full' : 'w-0',
                        )}
                        style={{
                          transitionDuration:
                            currentIndex === index
                              ? index === innovationImpacts.length - 1
                                ? `${collapseDelay / 2}ms`
                                : `${collapseDelay}ms`
                              : '0s',
                        }}
                      ></div>
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-[23px] right-auto top-0 mt-10 h-full w-0.5 overflow-visible rounded-lg bg-neutral-300/50 dark:bg-neutral-300/30 lg:hidden">
                    <div
                      className={cn(
                        'absolute left-0 top-0 w-full',
                        'via-gradient origin-top bg-gradient-to-b from-black to-white transition-all ease-linear',
                        currentIndex === index ? 'h-full' : 'h-0',
                      )}
                      style={{
                        transitionDuration:
                          currentIndex === index ? `${collapseDelay}ms` : '0s',
                      }}
                    ></div>
                  </div>
                  <AccordionTrigger className="mt-2 px-0 text-lg font-medium leading-[1.5] tracking-[-1px] text-white lg:mt-6 lg:px-4">
                    {item.heading}
                  </AccordionTrigger>
                  <div
                    className={cn(
                      'mb-4 mt-2 text-sm leading-[1.5] text-white/50 lg:mb-0 lg:max-w-[233px] lg:text-center',
                      currentIndex === index ? '' : 'opacity-0',
                    )}
                  >
                    {item.content}
                  </div>
                </AccordionItem>
              ))}
            </div>
          </Accordion.Root>
          <div
            className="absolute right-0 top-0 z-10 hidden h-full w-[90px] lg:block"
            style={{
              background:
                'linear-gradient(270deg, #141414 0%, rgba(20, 20, 20, 0.00) 100%)',
            }}
          ></div>
        </motion.div>
      </div>
    </section>
  );
}
